import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import {
  readMultiSelectionFromSession,
  readSharedDeviceTypeFromSession,
  readSingleStoreSelectionFromSession,
  writeMultiSelectionToSession,
  writeSharedDeviceTypeToSession,
  writeSingleStoreSelectionToSession,
} from "@/pages/store-insights/shared/storeInsightsSelectionSession";

const ALLOWED_DEVICE_TYPES = new Set<DeviceType>([
  "all",
  "FITTING_ROOM",
  "IN_AISLE",
  "MOBILE_PICKUP",
  "RFID_CENTRAL",
]);

type SelectionSession = { kind: "multi"; key: string } | { kind: "single"; key: string };

interface UseStoreInsightsDeviceTypeFilterOptions {
  enabled?: boolean;
  defaultDeviceType?: DeviceType;
  selectionSession: SelectionSession;
  selectionReady: boolean;
  preserveAllInUrl?: boolean;
}

const readSelectionDeviceType = (session: SelectionSession) =>
  session.kind === "multi"
    ? readMultiSelectionFromSession(session.key)?.deviceType
    : readSingleStoreSelectionFromSession(session.key)?.deviceType;

const writeSelectionDeviceType = (session: SelectionSession, deviceType: DeviceType) => {
  if (session.kind === "multi") {
    const selection = readMultiSelectionFromSession(session.key);
    writeMultiSelectionToSession(session.key, {
      customerIds: selection?.customerIds ?? [],
      groups: selection?.groups ?? [],
      storeIds: selection?.storeIds ?? [],
      deviceType,
    });
    return;
  }

  const selection = readSingleStoreSelectionFromSession(session.key);
  writeSingleStoreSelectionToSession(session.key, {
    storeId: selection?.storeId ?? null,
    deviceType,
    hasUserSelectedStore: selection?.hasUserSelectedStore,
  });
};

export const useStoreInsightsDeviceTypeFilter = ({
  enabled = true,
  defaultDeviceType = "all",
  selectionSession,
  selectionReady,
  preserveAllInUrl = false,
}: UseStoreInsightsDeviceTypeFilterOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [deviceType, setDeviceType] = useState<DeviceType>(() => {
    if (!enabled) return defaultDeviceType;

    const param = searchParams.get("deviceType");
    return param && ALLOWED_DEVICE_TYPES.has(param as DeviceType)
      ? (param as DeviceType)
      : (readSharedDeviceTypeFromSession() ??
          readSelectionDeviceType(selectionSession) ??
          defaultDeviceType);
  });
  const lastInternalSearchRef = useRef<string | null>(null);
  const currentSearch = searchParams.toString();
  const hasPendingInternalSearchUpdate = lastInternalSearchRef.current !== null;

  const updateDeviceTypeParam = useCallback(
    (nextDeviceType: DeviceType | null) => {
      const params = new URLSearchParams(searchParams);
      if (nextDeviceType === null) {
        params.delete("deviceType");
      } else {
        params.set("deviceType", nextDeviceType);
      }

      const nextSearch = params.toString();
      if (nextSearch === searchParams.toString()) return;

      lastInternalSearchRef.current = nextSearch;
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!enabled) {
      if (searchParams.has("deviceType")) updateDeviceTypeParam(null);
      if (deviceType !== defaultDeviceType) setDeviceType(defaultDeviceType);
      return;
    }

    const param = searchParams.get("deviceType");
    const nextDeviceType =
      param && ALLOWED_DEVICE_TYPES.has(param as DeviceType)
        ? (param as DeviceType)
        : !hasPendingInternalSearchUpdate
          ? (readSharedDeviceTypeFromSession() ??
            readSelectionDeviceType(selectionSession) ??
            defaultDeviceType)
          : deviceType;
    if (deviceType !== nextDeviceType) setDeviceType(nextDeviceType);

    const desiredParam = nextDeviceType === "all" && !preserveAllInUrl ? null : nextDeviceType;
    if (param !== desiredParam) updateDeviceTypeParam(desiredParam);

    if (selectionReady) {
      writeSelectionDeviceType(selectionSession, nextDeviceType);
      writeSharedDeviceTypeToSession(nextDeviceType);
    }
  }, [
    defaultDeviceType,
    deviceType,
    enabled,
    hasPendingInternalSearchUpdate,
    preserveAllInUrl,
    searchParams,
    selectionReady,
    selectionSession,
    updateDeviceTypeParam,
  ]);

  useEffect(() => {
    if (lastInternalSearchRef.current === currentSearch) {
      lastInternalSearchRef.current = null;
    }
  }, [currentSearch]);

  const onDeviceTypeChange = useCallback(
    (nextDeviceType: DeviceType) => {
      if (!enabled) return;

      setDeviceType(nextDeviceType);
      writeSelectionDeviceType(selectionSession, nextDeviceType);
      writeSharedDeviceTypeToSession(nextDeviceType);
      updateDeviceTypeParam(nextDeviceType === "all" && !preserveAllInUrl ? null : nextDeviceType);
    },
    [enabled, preserveAllInUrl, selectionSession, updateDeviceTypeParam],
  );

  return {
    deviceType,
    onDeviceTypeChange,
    deviceTypeFilterDisabled: !enabled,
  };
};
